import React from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Alert,
  Link,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ScreenRotationIcon from "@mui/icons-material/ScreenRotation";
import ColorLegend from "./ColorLegend";
import DataTable from "./DataTable";
import { styles, columnDescriptions, getScoreColor } from "./DataTable.styles";
import { FONT_SIZES } from "../../constants/fontSizes";

const tooltipStyles = {
  tooltip: {
    sx: {
      fontSize: FONT_SIZES.TEXT_HINT.xs,
      "@media (min-width: 600px)": {
        fontSize: FONT_SIZES.TEXT_HINT.sm,
      },
    },
  },
};

export const detailedColumns = [
  {
    id: "name",
    label: "Candidate Name",
    width: "15%",
  },
  {
    id: "mark_identification",
    label: "Matched Trademark",
    width: "15%",
    tooltip: columnDescriptions["Matched Trademark"],
    tooltipProps: tooltipStyles,
    format: (value, row) => {
      // If serial_number is NaN or falsy, show 'No existing trademark found'
      if (!row || isNaN(Number(row.serial_number))) {
        return "No existing trademark found";
      }
      return value;
    },
  },
  {
    id: "serial_number",
    label: "Serial Number",
    width: "10%",
    tooltip: columnDescriptions["Serial Number"],
    tooltipProps: tooltipStyles,
    format: (value) => {
      if (!value || isNaN(Number(value))) return "";
      return (
        <Link
          href={`https://tsdr.uspto.gov/#caseNumber=${value}&caseSearchType=US_APPLICATION&caseType=DEFAULT&searchType=statusSearch`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {value}
        </Link>
      );
    },
  },
  {
    id: "name_similarity_score",
    label: "Name Similarity",
    width: "10%",
    tooltip: columnDescriptions["Name Similarity"],
    tooltipProps: tooltipStyles,
    format: (value) => parseFloat(value) || 0,
    getColor: (value) => getScoreColor(parseFloat(value) || 0),
  },
  {
    id: "industry_similarity_score",
    label: "Industry Similarity",
    width: "10%",
    tooltip: columnDescriptions["Industry Similarity"],
    tooltipProps: tooltipStyles,
    format: (value) => parseFloat(value) || 0,
    getColor: (value) => getScoreColor(parseFloat(value) || 0),
  },
  {
    id: "legal_activity_score",
    label: "Legal Activity",
    width: "10%",
    tooltip: columnDescriptions["Legal Activity"],
    tooltipProps: tooltipStyles,
    format: (value) => parseFloat(value) || 0,
    getColor: (value) => getScoreColor(parseFloat(value) || 0),
  },
  {
    id: "statement_texts",
    label: "Trademark Product Description",
    width: "30%",
    tooltip: columnDescriptions["Trademark Product Description"],
    tooltipProps: tooltipStyles,
    format: (value) => {
      if (!value) return "Trademark Search Didn't Find Any Matches.";
      return (
        <Tooltip
          title={value}
          placement="top"
          arrow
          componentsProps={tooltipStyles}
        >
          <span style={styles.tooltipText}>
            <span className="trademark-desc-truncate">
              {value.length > 50 ? `${value.substring(0, 50)}...` : value}
            </span>
            <span className="trademark-desc-full">{value}</span>
          </span>
        </Tooltip>
      );
    },
  },
];

const DetailedTrademarkReport = ({
  showDetailedReport,
  detailedReportData,
  getDetailedRowColor,
  isMobile,
  flipColumns,
}) => {
  if (!showDetailedReport) {
    return (
      <>
        <Typography variant="h6" sx={styles.reportTitle}>
          Detailed Trademark Report
        </Typography>
        <Alert severity="info" icon={<ScreenRotationIcon />} sx={styles.alert}>
          Rotate your device to landscape mode to view report table.
        </Alert>
      </>
    );
  }

  return (
    <Accordion sx={styles.accordion}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6" sx={styles.accordionTitle}>
          Detailed Trademark Report
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <ColorLegend
          legendText={["Low Risk", "Medium Risk", "High Risk"]}
          legendColor={[
            styles.lowRiskColor,
            styles.mediumRiskColor,
            styles.highRiskColor,
          ]}
        />
        <DataTable
          columns={detailedColumns}
          data={detailedReportData}
          getRowColor={getDetailedRowColor}
          isReportData={false}
          sx={styles.dataTable}
          isMobile={isMobile}
          flipColumns={flipColumns}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default DetailedTrademarkReport;
